/**
 * Thin client for the arcadia84-api auth proxy endpoints (sc-3015).
 *
 * The shell does NOT ship a Zyphr API key — instead it talks to our
 * own server, which holds the Zyphr application secret. Same fetch
 * conventions as api/client.ts (X-Request-ID, JSON, errors decoded
 * to a structured shape).
 *
 * Endpoints used:
 *   POST /v1/auth/magic-link/send      → 204
 *   POST /v1/auth/magic-link/verify    → AuthSessionResponse
 *   POST /v1/auth/refresh              → AuthSessionResponse
 *   POST /v1/auth/logout               → 204
 */

import type { AuthSessionResponse } from '@arcraz/arcadia-types';

const SESSION_REQUEST_ID = newSessionId();

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function baseUrl(): string {
  const fromEnv = (import.meta.env?.VITE_API_BASE_URL as string | undefined)?.trim();
  if (fromEnv && fromEnv.length > 0) return fromEnv.replace(/\/+$/, '');
  return 'http://localhost:8084/v1';
}

export class AuthApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

async function parseError(resp: Response): Promise<AuthApiError> {
  let code = 'unknown';
  let message = resp.statusText || `HTTP ${resp.status}`;
  try {
    const body = (await resp.json()) as {
      error?: { code?: string; message?: string };
    };
    if (body.error) {
      code = body.error.code ?? code;
      message = body.error.message ?? message;
    }
  } catch {
    /* not JSON */
  }
  return new AuthApiError(resp.status, code, message);
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const resp = await fetch(`${baseUrl()}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Request-ID': SESSION_REQUEST_ID,
    },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw await parseError(resp);
  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

export async function sendMagicLink(args: {
  email: string;
  redirectUrl: string;
}): Promise<void> {
  await postJson<void>('/auth/magic-link/send', args);
}

export async function verifyMagicLink(token: string): Promise<AuthSessionResponse> {
  return postJson<AuthSessionResponse>('/auth/magic-link/verify', { token });
}

/** Exchange a provider OAuth code+state for a session (sc-6940). */
export async function verifyOAuthCallback(args: {
  provider: string;
  code: string;
  state: string;
}): Promise<AuthSessionResponse> {
  return postJson<AuthSessionResponse>('/auth/oauth/callback', args);
}

/**
 * Send an email-OTP code (sc-7039). The server transparently handles
 * login-vs-registration and returns an identical response either way — it
 * does NOT disclose whether the email has an account (no enumeration).
 */
export async function sendEmailOtp(email: string): Promise<void> {
  await postJson<{ ok: boolean }>('/auth/email-otp/send', { email });
}

/** Verify an email-OTP code for a session (sc-7039). */
export async function verifyEmailOtp(args: {
  email: string;
  code: string;
}): Promise<AuthSessionResponse> {
  return postJson<AuthSessionResponse>('/auth/email-otp/verify', args);
}

export async function refreshSession(
  refreshToken: string
): Promise<AuthSessionResponse> {
  return postJson<AuthSessionResponse>('/auth/refresh', { refreshToken });
}

export async function logoutSession(refreshToken: string): Promise<void> {
  await postJson<void>('/auth/logout', { refreshToken });
}
